import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Minus, Equal, AlertCircle, BookOpen, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, ReadOnlyBanner } from "@/components/common";
import EmployeePicker from "@/components/EmployeePicker";
import DatePicker, { isoToDmy } from "@/components/DatePicker";
import ConfirmDialog from "@/components/ConfirmDialog";
import BulkDeductions from "@/components/BulkDeductions";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, errMsg, errCode, newKey } from "@/lib/api";
import { inr, todayISO, currentMonth, monthLabel, toPaise, fmtDateTime } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

function CalcCard({ current, deduct, final, error, noLedger }) {
  return (
    <div className="rounded-2xl bg-slate-900 text-white p-6 sm:p-7 shadow-lg relative overflow-hidden" data-testid="deduction-calc-card">
      <div className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-blue-600/25 blur-2xl" />
      <div className="relative space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">Current outstanding{noLedger ? " (entered)" : ""}</span>
          <span className="money text-xl" data-testid="calc-current-balance">{inr(current / 100)}</span>
        </div>
        <div className="flex items-center justify-between text-emerald-300">
          <span className="text-[11px] uppercase tracking-wider flex items-center gap-1"><Minus className="h-3.5 w-3.5" /> Amount to deduct</span>
          <span className="money text-xl" data-testid="calc-deduction-amount">{inr(deduct / 100)}</span>
        </div>
        <div className="border-t border-white/15 pt-4">
          <div className="text-[11px] uppercase tracking-wider text-slate-400 flex items-center gap-1"><Equal className="h-3.5 w-3.5" /> Final outstanding balance</div>
          <div className={`money font-extrabold text-4xl sm:text-5xl mt-2 tracking-tight ${error ? "text-red-400" : "text-white"}`} data-testid="calc-final-balance">{inr(final / 100)}</div>
        </div>
        {error && <div className="flex items-start gap-2 text-sm text-red-300" data-testid="calc-error"><AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />{error}</div>}
      </div>
    </div>
  );
}

function SuccessDialog({ result, onClose }) {
  const t = result?.transaction;
  return (
    <Dialog open={!!result} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="deduction-success-dialog">
        <DialogHeader>
          <div className="h-12 w-12 rounded-full bg-emerald-50 grid place-items-center mb-2"><CheckCircle2 className="h-7 w-7 text-emerald-600" /></div>
          <DialogTitle>Deduction recorded successfully</DialogTitle>
          <DialogDescription>{t && `${t.employee_code} – ${t.employee_name}`}</DialogDescription>
        </DialogHeader>
        {t && (
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Deducted</div><div className="money font-bold text-emerald-700 text-lg" data-testid="success-deducted">{inr(t.credit)}</div></div>
            <div className="rounded-lg bg-slate-50 p-3"><div className="text-xs text-slate-500">Final outstanding</div><div className="money font-bold text-lg" data-testid="success-final-balance">{inr(result.balance_after)}</div></div>
            <div className="col-span-2 text-xs text-slate-500 space-y-0.5">
              <div>Reference: {t.reference || "—"} · Date: {isoToDmy(t.date)}</div>
              <div>Recorded by {t.created_by} at {fmtDateTime(t.created_at)}</div>
              {result.opening_created && <div className="text-blue-800">Opening balance of {inr(result.balance_before)} was also recorded.</div>}
            </div>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" asChild><Link to={`/ledger/${t?.employee_code}`} data-testid="success-view-ledger"><BookOpen className="h-4 w-4" /> View ledger</Link></Button>
          <Button className="bg-blue-800 hover:bg-blue-900" onClick={onClose} data-testid="success-next-employee">Next employee</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SingleDeduction() {
  const { can } = useAuth();
  const ro = !can("admin", "payroll");
  const [emp, setEmp] = useState(null);
  const [info, setInfo] = useState(null);
  const [amount, setAmount] = useState("");
  const [opening, setOpening] = useState("");
  const [date, setDate] = useState(todayISO());
  const [reference, setReference] = useState(`Payroll ${monthLabel(currentMonth())}`);
  const [remarks, setRemarks] = useState("");
  const [key, setKey] = useState(newKey());
  const [ask, setAsk] = useState(false);
  const [dup, setDup] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(true);

  useEffect(() => {
    setInfo(null); setAmount(""); setOpening(""); setKey(newKey());
    if (emp) api.get(`/employees/${emp.code}`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [emp]);

  const noLedger = info && info.summary.transaction_count === 0;
  const current = info ? (noLedger ? toPaise(opening) ?? 0 : Math.round(info.summary.current_balance * 100)) : 0;
  const deduct = toPaise(amount) ?? 0;
  const final = current - deduct;
  let error = "";
  if (amount && toPaise(amount) === null) error = "Enter a valid amount.";
  else if (amount && deduct <= 0) error = "Deduction amount must be greater than zero.";
  else if (deduct > current) error = `Deduction cannot exceed the current outstanding balance (${inr(current / 100)}).`;
  if (noLedger && opening && toPaise(opening) === null) error = "Enter a valid opening balance.";
  const ready = info && deduct > 0 && !error && current > 0 && date;

  const submit = async (allowDuplicate = false) => {
    setBusy(true);
    try {
      const { data } = await api.post("/transactions", {
        type: "deduction", employee_code: emp.code, amount, date, reference, remarks, idempotency_key: key,
        opening_balance: noLedger ? opening : null, allow_duplicate: allowDuplicate,
      });
      setAsk(false); setDup(null); setResult(data);
      toast.success(`Deduction of ${inr(data.transaction.credit)} recorded`);
    } catch (e) {
      if (errCode(e) === "DUPLICATE_SUSPECT") { setAsk(false); setDup(errMsg(e)); }
      else toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const nextEmployee = () => { setResult(null); setRemarks(""); setEmp(null); setPickerOpen(true); };

  return (
    <div>
      <div className="grid lg:grid-cols-5 gap-5 fade-up">
        <div className="lg:col-span-3 bg-white rounded-xl border border-slate-200 p-5 sm:p-6 space-y-5">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">1. Employee</Label>
            <div className="mt-1.5"><EmployeePicker value={emp} onChange={(e) => { setEmp(e); setPickerOpen(false); }} autoOpen={pickerOpen && !emp && !ro} testid="deduction-employee-picker" /></div>
            {info && (
              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm rounded-lg bg-slate-50 border border-slate-200 px-3 py-2" data-testid="deduction-employee-info">
                <span className="flex items-center gap-1.5"><UserRound className="h-4 w-4 text-slate-400" /><span className="money text-blue-800 font-semibold">{info.code}</span> {info.name}</span>
                {info.department && <span className="text-slate-500">{info.department}</span>}
                {info.branch && <span className="text-slate-500">{info.branch}</span>}
                {info.status === "inactive" && <span className="text-xs text-slate-500 border rounded px-1.5">Inactive</span>}
              </div>
            )}
          </div>
          {noLedger && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4" data-testid="no-ledger-box">
              <div className="text-sm text-amber-800 font-medium">No ledger yet for this employee</div>
              <div className="text-xs text-amber-700 mb-2">Enter the current outstanding advance (from Attendo). It will be saved as the Opening Balance.</div>
              <Input inputMode="decimal" placeholder="Opening outstanding balance ₹" className="bg-white money h-11" value={opening} onChange={(e) => setOpening(e.target.value)} disabled={ro} data-testid="deduction-opening-input" />
            </div>
          )}
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">2. Amount to deduct (₹)</Label>
            <Input inputMode="decimal" placeholder="0.00" className="mt-1.5 h-14 text-2xl money font-semibold" value={amount} onChange={(e) => setAmount(e.target.value)} disabled={ro || !info}
              onKeyDown={(e) => { if (e.key === "Enter" && ready) setAsk(true); }} data-testid="deduction-amount-input" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Reference / Payroll month</Label><Input className="mt-1.5" value={reference} onChange={(e) => setReference(e.target.value)} disabled={ro} data-testid="deduction-reference-input" /></div>
            <div><Label>Deduction date</Label><DatePicker max={todayISO()} className="mt-1.5 h-10" value={date} onChange={setDate} disabled={ro} testid="deduction-date-input" /></div>
            <div className="sm:col-span-2"><Label>Remarks (optional)</Label><Input className="mt-1.5" value={remarks} onChange={(e) => setRemarks(e.target.value)} disabled={ro} data-testid="deduction-remarks-input" /></div>
          </div>
        </div>
        <div className="lg:col-span-2 space-y-4">
          <CalcCard current={current} deduct={deduct} final={final} error={error} noLedger={noLedger} />
          <Button className="w-full h-14 text-base font-semibold bg-blue-800 hover:bg-blue-900" disabled={ro || !ready || busy} onClick={() => setAsk(true)} data-testid="record-deduction-btn">
            {busy && <Loader2 className="h-5 w-5 animate-spin" />} Record Deduction
          </Button>
          {info && !noLedger && <Link to={`/ledger/${info.code}`} className="block text-center text-sm text-blue-800 hover:underline" data-testid="deduction-view-ledger-link">View {info.name}'s ledger</Link>}
        </div>
      </div>
      {info && !ro && (
        <div className="lg:hidden fixed bottom-0 inset-x-0 z-20 bg-slate-900 text-white px-4 py-3 flex items-center justify-between gap-3 shadow-2xl" data-testid="mobile-final-bar">
          <div><div className="text-[10px] uppercase tracking-wider text-slate-400">Final outstanding</div><div className={`money text-xl font-bold ${error ? "text-red-400" : ""}`}>{inr(final / 100)}</div></div>
          <Button className="bg-blue-700 hover:bg-blue-800" disabled={!ready || busy} onClick={() => setAsk(true)} data-testid="mobile-record-deduction-btn">Record</Button>
        </div>
      )}
      {info && !ro && <div className="h-20 lg:hidden" />}
      <ConfirmDialog open={ask} onOpenChange={setAsk} busy={busy} onConfirm={() => submit(false)} confirmText="Yes, record deduction" testid="deduction-confirm-dialog"
        title="Confirm advance deduction" description={emp && `${emp.code} – ${emp.name}`}>
        <div className="rounded-lg bg-slate-50 p-4 text-sm space-y-1.5">
          {noLedger && <div className="flex justify-between"><span>Opening balance (new)</span><span className="money">{inr(current / 100)}</span></div>}
          <div className="flex justify-between"><span>Current outstanding</span><span className="money">{inr(current / 100)}</span></div>
          <div className="flex justify-between text-emerald-700"><span>Deduction</span><span className="money">− {inr(deduct / 100)}</span></div>
          <div className="flex justify-between font-bold border-t pt-1.5"><span>Final outstanding</span><span className="money">{inr(final / 100)}</span></div>
          <div className="text-xs text-slate-500 pt-1">Reference: {reference || "—"} · Date: {isoToDmy(date)}</div>
        </div>
      </ConfirmDialog>
      <ConfirmDialog open={!!dup} onOpenChange={(o) => !o && setDup(null)} busy={busy} onConfirm={() => submit(true)} tone="warning" confirmText="Record anyway" testid="duplicate-warning-dialog"
        title="Possible duplicate deduction" description={dup} />
      <SuccessDialog result={result} onClose={nextEmployee} />
    </div>
  );
}

export default function Deduction() {
  const { can } = useAuth();
  const ro = !can("admin", "payroll");
  return (
    <div>
      <PageHeader title="Advance Deduction" subtitle="Record one deduction with the calculator, or upload an Excel file to record many at once." />
      {ro && <ReadOnlyBanner />}
      <Tabs defaultValue="single">
        <TabsList className="mb-4">
          <TabsTrigger value="single" data-testid="deduction-tab-single">Single deduction</TabsTrigger>
          <TabsTrigger value="bulk" data-testid="deduction-tab-bulk" disabled={ro}>Bulk upload (Excel/CSV)</TabsTrigger>
        </TabsList>
        <TabsContent value="single"><SingleDeduction /></TabsContent>
        <TabsContent value="bulk"><BulkDeductions disabled={ro} /></TabsContent>
      </Tabs>
    </div>
  );
}
